import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { StickyFooter } from '../components/StickyFooter';
import { StarIcon, MapPinIcon, ShieldCheckIcon, CalendarIcon } from 'lucide-react';

// Mock Data
const MOCK_CAREGIVERS = [{
  id: 1,
  name: 'Sarah Jenkins',
  role: 'Registered Nurse (RN)',
  distance: '2.4 miles away',
  rating: 4.9,
  reviews: 124,
  rate: '$45/hr',
  image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&auto=format&fit=crop',
  badges: ['CPR Certified', 'Background Checked']
}, {
  id: 2,
  name: 'Michael Chen',
  role: 'Personal Support Worker (PSW)',
  distance: '3.1 miles away',
  rating: 4.8,
  reviews: 89,
  rate: '$28/hr',
  image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&auto=format&fit=crop',
  badges: ['Dementia Care', 'Background Checked']
}, {
  id: 3,
  name: 'Emily Rodriguez',
  role: 'Personal Support Worker (PSW)',
  distance: '5.0 miles away',
  rating: 5.0,
  reviews: 210,
  rate: '$30/hr',
  image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400&auto=format&fit=crop',
  badges: ['Post-Hospital Care', 'Background Checked']
}];
const MOCK_NANNIES = [{
  id: 1,
  name: 'Jessica Taylor',
  role: 'Full-Time Nanny',
  distance: '1.2 miles away',
  rating: 4.9,
  reviews: 156,
  rate: '$25/hr',
  image: 'https://images.unsplash.com/photo-1544281679-44463372c544?w=400&auto=format&fit=crop',
  badges: ['CPR Certified', 'Early Childhood Ed']
}, {
  id: 2,
  name: 'David Smith',
  role: 'Part-Time Babysitter',
  distance: '4.5 miles away',
  rating: 4.7,
  reviews: 62,
  rate: '$20/hr',
  image: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=400&auto=format&fit=crop',
  badges: ['First-Aid Certified', 'Background Checked']
}];
const MOCK_RENTALS = [{
  id: 1,
  name: 'Premium Wheelchair',
  category: 'Mobility Equipment',
  distance: 'Delivered from local hub',
  rating: 4.8,
  reviews: 340,
  rate: '$25/day',
  image: 'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=400&auto=format&fit=crop',
  badges: ['Sanitized', 'Same-Day Delivery']
}, {
  id: 2,
  name: 'Travel Stroller',
  category: 'Baby Gear',
  distance: 'Delivered from local hub',
  rating: 4.9,
  reviews: 128,
  rate: '$15/day',
  image: 'https://images.unsplash.com/photo-1519689680058-324335c77eba?w=400&auto=format&fit=crop',
  badges: ['Sanitized', 'Compact Fold']
}, {
  id: 3,
  name: 'Hospital Bed (Adjustable)',
  category: 'Medical Equipment',
  distance: 'Delivered from local hub',
  rating: 5.0,
  reviews: 85,
  rate: '$40/day',
  image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&auto=format&fit=crop',
  badges: ['Professional Setup', 'Sanitized']
}];
export function SearchResults() {
  const location = useLocation();
  const [results, setResults] = useState<any[]>([]);
  const [type, setType] = useState('');
  const [postalCode, setPostalCode] = useState('');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchType = params.get('type') || 'caregivers';
    const searchLocation = params.get('location') || '';
    setType(searchType);
    setPostalCode(searchLocation);
    if (searchType === 'nanny') {
      setResults(MOCK_NANNIES);
    } else if (searchType === 'rentals') {
      setResults(MOCK_RENTALS);
    } else {
      setResults(MOCK_CAREGIVERS);
    }
  }, [location]);
  const getTitle = () => {
    switch (type) {
      case 'nanny':
        return 'Available Nannies & Babysitters';
      case 'rentals':
        return 'Available Equipment Rentals';
      case 'caregivers':
      default:
        return 'Available Caregivers & Nurses';
    }
  };
  return <div className="min-h-screen w-full bg-gray-50 flex flex-col" data-id="element-2140" data-mp="ixmkytu1ouay">
      <Navbar data-id="element-2141" data-mp="29df2wt8u1710" />
      
      <main className="flex-1 py-12" data-id="element-2142" data-mp="29cww52ojnaeu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2143" data-mp="29g1b8v3jrqka">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-2144" data-mp="24h7turl53feu">
            <div data-id="element-2145" data-mp="2icsdjp8p01nb">
              <h1 className="text-3xl font-bold text-[#1a2332] mb-2" data-id="element-2146" data-mp="2edb6wshj4ygk">{getTitle()}</h1>
              <p className="text-[#5a6a7a] flex items-center gap-2" data-id="element-2147" data-mp="2eb6x5twy958d">
                <MapPinIcon className="w-4 h-4" data-id="element-2148" data-mp="1qo8gav4x3v9x" />
                Showing results near <span className="font-semibold text-[#1a2332]" data-id="element-2149" data-mp="1lpf210ht408p">{postalCode || 'your location'}</span>
              </p>
            </div>
            <div className="flex gap-2" data-id="element-2150" data-mp="1zhlkit8fmjbb">
              <Link to="/" className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-[#1a2332] hover:bg-gray-50 transition-colors" data-id="element-2151" data-mp="1upk919j38wmc">
                Change Search
              </Link>
            </div>
          </div>

          {/* Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2152" data-mp="12wbg61atyron">
            {results.map(item => <div key={item.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-2153" data-mp="xknw36aud8ks">
                <div className="h-48 overflow-hidden relative" data-id="element-2154" data-mp="itx2pttlq1fk">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-2155" data-mp="nw5yexxuzhld" />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1 text-sm font-bold text-[#1a2332]" data-id="element-2156" data-mp="1blqkvqt80wze">
                    <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-2157" data-mp="11mrjmtasc3mf" />
                    {item.rating} <span className="text-gray-500 font-normal" data-id="element-2158" data-mp="16lc9pzwbp6x5">({item.reviews})</span>
                  </div>
                </div>
                
                <div className="p-5 flex-1 flex flex-col" data-id="element-2159" data-mp="3mb6w44xlrv2o">
                  <div className="flex justify-between items-start mb-2" data-id="element-2160" data-mp="3rccq06301ods">
                    <div data-id="element-2161" data-mp="3r9guspijddt1">
                      <h3 className="font-bold text-lg text-[#1a2332]" data-id="element-2162" data-mp="1xtm4x8gesz9c">{item.name}</h3>
                      <p className="text-[#007a87] font-medium text-sm" data-id="element-2163" data-mp="1su9lkxoqyabi">{item.role || item.category}</p>
                    </div>
                    <div className="text-right" data-id="element-2164" data-mp="1o29f0me68j5y">
                      <div className="font-bold text-lg text-[#1a2332]" data-id="element-2165" data-mp="1o0ycsr0djjgy">{item.rate}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-4" data-id="element-2166" data-mp="1e5wmvsy9yddb">
                    <MapPinIcon className="w-4 h-4" data-id="element-2167" data-mp="1a2gr0yu14h1o" />
                    {item.distance}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-2168" data-mp="15588uy8vsd0f">
                    {item.badges.map((badge: string, idx: number) => <span key={idx} className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1" data-id="element-2169" data-mp="107ow62ru08al" data-mpi={idx}>
                        <ShieldCheckIcon className="w-3 h-3 text-[#007a87]" data-id="element-2170" data-mp="2ukeowh6grbge" />
                        {badge}
                      </span>)}
                  </div>

                  <button className="mt-auto w-full bg-[#5a55ea] hover:bg-[#4b47c4] text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-2171" data-mp="371h1cukz7o1i">
                    <CalendarIcon className="w-4 h-4" data-id="element-2172" data-mp="3grsgzee0vlo8" />
                    Book {type === 'rentals' ? 'Rental' : item.name.split(' ')[0]}
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </main>

      <StickyFooter data-id="element-2173" data-mp="2o3mv2ob8fqmt" />
    </div>;
}